// src/features/mech/plastics/questions/PartDetailsQuestion.jsx
import React, { forwardRef, useImperativeHandle, useEffect } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlSelect from "../../../../shared/components/PerlSelect";
import PerlAddButton from "../../../../shared/components/PerlAddButton";

const PartDetailsQuestion = forwardRef(({ value, onChange, editingLayerIndex, onEditComplete }, ref) => {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const detailsValue = value || {
    regrindPct: "",
    newNumberField: "",
    avgWallThickness: "",
    burstingFactor: "",
    layers: [],
  };

  const FILM_STRUCTURE_OPTIONS = [
    "PET",
    "PE",
    "ADHESIVE",
    "INK",
    "Pearlized BOPP",
    "CPP",
    "MPET",
    "Paper",
    "LDPE",
    "Foil",
    "Met BOPP",
    "Extrusion ",
    "Solvent-based gravure ink",
    "PU solvent-based lamination adhesive",
  ];

  const LAYER_TYPE_OPTIONS = ["film", "ink", "adhesive"];

  // Check if all required fields are filled - use current value prop
  const isAllFieldsFilled = () => {
    // Get current value (will be updated on each render)
    const currentValue = value || {
      regrindPct: "",
      newNumberField: "",
      avgWallThickness: "",
      burstingFactor: "",
      layers: [],
    };
    
    // At least one layer must be added
    if (!currentValue.layers || currentValue.layers.length === 0) return false;
    
    return true;
  };

  // Expose validate method to parent
  useImperativeHandle(ref, () => ({
    validate: () => true,
    isValid: isAllFieldsFilled,
  }), [value]); // Add value as dependency to update when it changes

  const handleFieldChange = (field, newValue) => {
    const updatedValue = {
      ...detailsValue,
      [field]: newValue,
    };
    
    // If the Layers number field changes, generate/update the layers array
    if (field === "newNumberField") {
      const numLayers = parseInt(newValue) || 0;
      const existingLayers = detailsValue.layers || [];
      
      // Generate layers array based on the number
      const generatedLayers = [];
      for (let i = 1; i <= numLayers; i++) {
        // If layer already exists, keep its values; otherwise create new
        const existingLayer = existingLayers.find(l => l.layerName === `Layer ${i}`);
        generatedLayers.push({
          layerName: `Layer ${i}`,
          filmStructure: existingLayer?.filmStructure || "",
          type: existingLayer?.type || "",
          microns: existingLayer?.microns || "",
        });
      }
      
      updatedValue.layers = generatedLayers;
    }
    
    onChange?.(updatedValue);
  };

  // Handle changes to individual layer fields in the table
  const handleLayerFieldChange = (index, field, newValue) => {
    const existingLayers = [...(detailsValue.layers || [])];
    if (existingLayers[index]) {
      existingLayers[index] = {
        ...existingLayers[index],
        [field]: newValue,
      };
      
      const updatedValue = {
        ...detailsValue,
        layers: existingLayers,
      };
      onChange?.(updatedValue);
    }
  };

  const handleAddLayer = () => {
    const filmStructure = detailsValue.avgWallThickness || "";
    const microns = detailsValue.burstingFactor || "";

    // Validate that both fields have values
    if (!filmStructure || !microns) {
      return;
    }

    // Get existing layers or initialize empty array
    const existingLayers = detailsValue.layers || [];
    
    let updatedLayers;
    
    // If editing an existing layer, update it; otherwise add a new one
    if (editingLayerIndex !== null && editingLayerIndex >= 0 && editingLayerIndex < existingLayers.length) {
      // Update existing layer
      updatedLayers = [...existingLayers];
      updatedLayers[editingLayerIndex] = {
        ...updatedLayers[editingLayerIndex],
        filmStructure: filmStructure,
        microns: microns,
      };
      // Clear edit state
      onEditComplete?.();
    } else {
      // Add new layer
      const layerNumber = existingLayers.length + 1;
      const newLayer = {
        layerName: `Layer ${layerNumber}`,
        filmStructure: filmStructure,
        microns: microns,
      };
      updatedLayers = [...existingLayers, newLayer];
    }

    const updatedValue = {
      ...detailsValue,
      layers: updatedLayers,
      avgWallThickness: "", // Clear film structure
      burstingFactor: "", // Clear microns
    };

    onChange?.(updatedValue);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <PerlTextField
        label="Layers"
        type="number"
        value={detailsValue.newNumberField || ""}
        onChange={(e) => handleFieldChange("newNumberField", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      
      {/* Display table when layers number is provided */}
      {detailsValue.newNumberField && parseInt(detailsValue.newNumberField) > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: "transparent",
            backgroundImage: "none",
            boxShadow: "none",
            border: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
            borderRadius: 1.5,
            mt: 1,
          }}
        >
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    color: perlColors.textSecondary,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                    py: 1,
                    width: "20%",
                    minWidth: "100px",
                  }}
                >
                  Layers
                </TableCell>
                <TableCell
                  sx={{
                    color: perlColors.textSecondary,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                    py: 1,
                    width: "35%",
                    minWidth: "180px",
                  }}
                >
                  Structures
                </TableCell>
                <TableCell
                  sx={{
                    color: perlColors.textSecondary,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                    py: 1,
                    width: "20%",
                    minWidth: "120px",
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    color: perlColors.textSecondary,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
                    py: 1,
                    width: "25%",
                    minWidth: "120px",
                  }}
                >
                  Microns
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(detailsValue.layers || []).map((layer, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      bgcolor: alpha(brandColor, 0.05),
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: perlColors.textPrimary,
                      fontSize: "0.875rem",
                      borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                      py: 1,
                      width: "20%",
                      minWidth: "100px",
                    }}
                  >
                    {layer.layerName}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: perlColors.textPrimary,
                      fontSize: "0.875rem",
                      borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                      py: 1,
                      width: "35%",
                      minWidth: "180px",
                    }}
                  >
                    <PerlSelect
                      value={layer.filmStructure || ""}
                      onChange={(e) => handleLayerFieldChange(index, "filmStructure", e.target.value)}
                      options={FILM_STRUCTURE_OPTIONS}
                      showLabel={false}
                      size="small"
                      fullWidth={true}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: perlColors.textPrimary,
                      fontSize: "0.875rem",
                      borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                      py: 1,
                      width: "20%",
                      minWidth: "120px",
                    }}
                  >
                    <PerlSelect
                      value={layer.type || ""}
                      onChange={(e) => handleLayerFieldChange(index, "type", e.target.value)}
                      options={LAYER_TYPE_OPTIONS}
                      showLabel={false}
                      size="small"
                      fullWidth={true}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: perlColors.textPrimary,
                      fontSize: "0.875rem",
                      borderBottom: `1px solid ${alpha(perlColors.textPrimary, 0.08)}`,
                      py: 1,
                      width: "25%",
                      minWidth: "120px",
                    }}
                  >
                    <PerlTextField
                      type="number"
                      value={layer.microns || ""}
                      onChange={(e) => handleLayerFieldChange(index, "microns", e.target.value)}
                      showLabel={false}
                      size="small"
                      fullWidth={true}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Commented out - Old fields replaced by table-based approach
      <PerlSelect
        label="Substrate"
        value={detailsValue.avgWallThickness || ""}
        onChange={(e) => handleFieldChange("avgWallThickness", e.target.value)}
        // placeholder="Substrate"
        options={FILM_STRUCTURE_OPTIONS}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <PerlTextField
          label="Microns"
          type="number"
          value={detailsValue.burstingFactor || ""}
          onChange={(e) => handleFieldChange("burstingFactor", e.target.value)}
          InputProps={{
            sx: {
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
              "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
              "&.Mui-focused fieldset": {
                borderColor: brandColor,
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
            },
          }}
          InputLabelProps={{
            sx: {
              color: perlColors.textSecondary,
              fontSize: "0.85rem",
              letterSpacing: "0.3px",
              "&.Mui-focused": { color: perlColors.textPrimary },
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PerlAddButton onClick={handleAddLayer} />
        </Box>
      </Box>
      */}
    </Box>
  );
});

PartDetailsQuestion.displayName = "PartDetailsQuestion";

export default PartDetailsQuestion;

